// src/pages/team/ScheduleNewGame.js
import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";

function ScheduleNewGame({ teamId, onGameSaved, gameToEdit, onCancelEdit }) {
  const isEditMode = Boolean(gameToEdit);

  // Get today's date for defaults
  const today = new Date();
  const defaultYear = today.getFullYear();
  const defaultMonth = today.getMonth() + 1;
  const defaultDay = today.getDate();

  // Convert current time to 12-hour format
  let currentHour24 = today.getHours();
  let defaultHour = currentHour24;
  let defaultPeriod = "AM";
  if (currentHour24 === 0) {
    defaultHour = 12;
    defaultPeriod = "AM";
  } else if (currentHour24 === 12) {
    defaultPeriod = "PM";
  } else if (currentHour24 > 12) {
    defaultHour = currentHour24 - 12;
    defaultPeriod = "PM";
  } else {
    defaultPeriod = "AM";
  }
  const defaultMinute = Math.floor(today.getMinutes() / 5) * 5;

  // Form state for text fields
  const [opponentName, setOpponentName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [gameStatus, setGameStatus] = useState("scheduled");
  const [notes, setNotes] = useState("");

  // State for dropdowns
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedDay, setSelectedDay] = useState(defaultDay);
  const [selectedHour, setSelectedHour] = useState(defaultHour);
  const [selectedMinute, setSelectedMinute] = useState(defaultMinute);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Recent venues for quick selection
  const [recentVenues, setRecentVenues] = useState([
    { name: "Home Field", address: "123 Home St, Hometown" },
    { name: "City Stadium", address: "456 Stadium Ave, Metropolis" },
    { name: "Sports Complex", address: "789 Sports Blvd, Athletica" }
  ]);

  // Toggle advanced fields
  const [showAdvanced, setShowAdvanced] = useState(false);

  // When gameToEdit changes, preload the form fields.
  useEffect(() => {
    if (isEditMode && gameToEdit) {
      setOpponentName(gameToEdit.opponentName || "");
      setLocationName(gameToEdit.locationName || "");
      setLocationAddress(gameToEdit.locationAddress || "");
      setGameStatus(gameToEdit.status || "scheduled");
      setNotes(gameToEdit.notes || "");
      
      // Assume gameToEdit.date is an ISO string
      const editDate = new Date(gameToEdit.date);
      setSelectedYear(editDate.getFullYear());
      setSelectedMonth(editDate.getMonth() + 1);
      setSelectedDay(editDate.getDate());
      
      // Convert edit hour from 24-hour to 12-hour format
      let hour24 = editDate.getHours();
      let hour12 = hour24;
      let period = "AM";
      if (hour24 === 0) {
        hour12 = 12;
        period = "AM";
      } else if (hour24 === 12) {
        period = "PM";
      } else if (hour24 > 12) {
        hour12 = hour24 - 12;
        period = "PM";
      } else {
        period = "AM";
      }
      setSelectedHour(hour12);
      setSelectedMinute(Math.floor(editDate.getMinutes() / 5) * 5);
      setSelectedPeriod(period);
    } else {
      // Reset to defaults when not editing
      setOpponentName("");
      setLocationName("");
      setLocationAddress("");
      setGameStatus("scheduled");
      setNotes("");
      setSelectedYear(defaultYear);
      setSelectedMonth(defaultMonth);
      setSelectedDay(defaultDay);
      setSelectedHour(defaultHour);
      setSelectedMinute(defaultMinute);
      setSelectedPeriod(defaultPeriod);
    }
  }, [gameToEdit, isEditMode, defaultYear, defaultMonth, defaultDay, defaultHour, defaultMinute, defaultPeriod]);

  // Generate dropdown options
  const years = [];
  for (let i = defaultYear - 2; i <= defaultYear + 2; i++) {
    years.push(i);
  }
  const months = [
    { value: 1, name: "January" },
    { value: 2, name: "February" },
    { value: 3, name: "March" },
    { value: 4, name: "April" },
    { value: 5, name: "May" },
    { value: 6, name: "June" },
    { value: 7, name: "July" },
    { value: 8, name: "August" },
    { value: 9, name: "September" },
    { value: 10, name: "October" },
    { value: 11, name: "November" },
    { value: 12, name: "December" },
  ];
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const days = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  const hours = [];
  for (let i = 1; i <= 12; i++) {
    hours.push(i);
  }
  const minutes = [];
  for (let i = 0; i < 60; i += 5) {
    minutes.push(i);
  }
  const periods = ["AM", "PM"];

  const handleVenueSelect = (venue) => {
    setLocationName(venue.name);
    setLocationAddress(venue.address);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess("");
    
    try {
      // Convert 12-hour time to 24-hour time
      let hour24 = selectedHour;
      if (selectedPeriod === "PM" && selectedHour < 12) {
        hour24 = selectedHour + 12;
      }
      if (selectedPeriod === "AM" && selectedHour === 12) {
        hour24 = 0;
      }
      const fullDate = new Date(
        selectedYear,
        selectedMonth - 1,
        selectedDay,
        hour24,
        selectedMinute
      );
      const dateStr = fullDate.toISOString();
      const timeStr = fullDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
  
      // Simplified game data - no home/away distinction
      const gameData = {
        teamId: teamId,  // The team scheduling the game
        opponentName,
        date: dateStr,
        time: timeStr,
        locationName,
        locationAddress,
        status: gameStatus,
        notes: notes || "",
        yourScore: 0,     // Initialize scores to 0
        opponentScore: 0  // Initialize scores to 0
      };
      
      if (isEditMode) {
        // Update existing game
        const gameRef = doc(db, "games", gameToEdit.id);
        
        // Include timestamp for updates
        const updateData = {
          ...gameData,
          lastUpdated: new Date().toISOString()
        };
        
        await updateDoc(gameRef, updateData);
        
        if (onGameSaved) {
          onGameSaved({
            id: gameToEdit.id,
            ...updateData
          });
        }
        
        setSuccess("Game updated successfully!");
      } else {
        // Include timestamps for new game
        const newGameData = {
          ...gameData,
          createdAt: new Date().toISOString()
        };
        
        // Create game with a single operation
        const docRef = await addDoc(collection(db, "games"), newGameData);
        
        // Add the ID to the callback data without updating the document
        const completeGameData = {
          id: docRef.id,
          ...newGameData
        };
        
        if (onGameSaved) {
          onGameSaved(completeGameData);
        }
        
        setSuccess("New game scheduled successfully!");
        
        // Save this venue to recent venues if it's not already there
        if (locationName && locationAddress && 
            !recentVenues.some(v => v.name === locationName && v.address === locationAddress)) {
          setRecentVenues(prev => [
            { name: locationName, address: locationAddress },
            ...prev.slice(0, 4) // Keep only the 5 most recent venues
          ]);
        }
      }
  
      // Reset form fields after a short delay
      setTimeout(() => {
        if (!isEditMode) {
          setOpponentName("");
          setSelectedYear(defaultYear);
          setSelectedMonth(defaultMonth);
          setSelectedDay(defaultDay);
          setSelectedHour(defaultHour);
          setSelectedMinute(defaultMinute);
          setSelectedPeriod(defaultPeriod);
          setLocationName("");
          setLocationAddress("");
          setGameStatus("scheduled");
          setNotes("");
        }
        setSuccess("");
      }, 3000);
      
    } catch (err) {
      console.error("Game save failed:", err);
      let errorMessage = `Failed to save game: ${err.message}`;
      if (err.code === "permission-denied") {
        errorMessage = "You don't have permission to manage games for this team. Please contact a team administrator.";
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Color scheme
  const colors = {
    primary: "#3b82f6", // Blue
    primaryDark: "#1d4ed8",
    primaryLight: "#dbeafe",
    secondary: "#f97316", // Orange
    success: "#10b981", // Green
    error: "#ef4444", // Red
    text: "#1e293b",
    textLight: "#64748b",
    border: "#e2e8f0",
    background: "#f8fafc",
    white: "#ffffff",
  };

  // Enhanced styling with color scheme
  const containerStyle = {
    backgroundColor: colors.white,
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    maxWidth: "640px",
    margin: "2rem auto",
    fontFamily: "Inter, system-ui, sans-serif",
  };
  
  const formGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    marginBottom: "1rem",
  };
  
  const labelStyle = {
    display: "block",
    marginBottom: "0.5rem",
    fontWeight: "500",
    color: colors.text,
    fontSize: "0.875rem",
  };
  
  const inputStyle = {
    width: "100%",
    padding: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    fontSize: "1rem",
    transition: "all 0.2s ease",
    outline: "none",
  };
  
  const focusedInputStyle = {
    ...inputStyle,
    borderColor: colors.primary,
    boxShadow: `0 0 0 3px ${colors.primaryLight}`,
  };
  
  const dropdownStyle = {
    flex: "1",
    padding: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    fontSize: "1rem",
    outline: "none",
  };
  
  const primaryButtonStyle = {
    width: "100%",
    padding: "0.875rem",
    backgroundColor: colors.primary,
    color: colors.white,
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.5rem",
  };
  
  const secondaryButtonStyle = {
    ...primaryButtonStyle,
    backgroundColor: "transparent",
    border: `1px solid ${colors.border}`,
    color: colors.text,
  };
  
  const venueButtonStyle = {
    padding: "0.5rem 1rem",
    backgroundColor: colors.primaryLight,
    color: colors.primary,
    border: "none",
    borderRadius: "6px",
    fontSize: "0.875rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginRight: "0.5rem",
    marginBottom: "0.5rem",
  };

  return (
    <div style={containerStyle}>
      <h2 style={{ textAlign: "center", marginBottom: "1.5rem", color: colors.text, fontSize: "1.875rem", fontWeight: "600" }}>
        {isEditMode ? "Edit Game" : "Schedule New Game"}
      </h2>
      
      {success && (
        <div style={{ 
          backgroundColor: "#ecfdf5", 
          color: "#065f46", 
          padding: "1rem", 
          borderRadius: "8px", 
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "center" 
        }}>
          <span style={{ marginRight: "0.5rem" }}>✓</span>
          {success}
        </div>
      )}
      
      {error && (
        <div style={{ 
          backgroundColor: "#fef2f2", 
          color: "#991b1b", 
          padding: "1rem", 
          borderRadius: "8px", 
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "center" 
        }}>
          <span style={{ marginRight: "0.5rem" }}>⚠️</span>
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1.5rem" }}>
        {/* Opponent Name */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Opponent Name *</label>
          <input
            type="text"
            value={opponentName}
            onChange={(e) => setOpponentName(e.target.value)}
            required
            style={inputStyle}
            placeholder="Enter opponent's team name"
          />
        </div>
        
        {/* Date and Time Section */}
        <div style={{ 
          backgroundColor: colors.background, 
          padding: "1.5rem", 
          borderRadius: "8px",
          marginBottom: "1rem"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem", color: colors.text }}>
            Date & Time
          </h3>
          
          {/* Date Dropdowns */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Game Date *</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={dropdownStyle}
                aria-label="Month"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                style={dropdownStyle}
                aria-label="Day"
              >
                {days.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={dropdownStyle}
                aria-label="Year"
              >
                {years.map((yr) => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Time Dropdowns */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Game Time *</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <select
                value={selectedHour}
                onChange={(e) => setSelectedHour(parseInt(e.target.value))}
                style={dropdownStyle}
                aria-label="Hour"
              >
                {hours.map((h) => (
                  <option key={h} value={h}>
                    {h.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
              <select
                value={selectedMinute}
                onChange={(e) => setSelectedMinute(parseInt(e.target.value))}
                style={dropdownStyle}
                aria-label="Minute"
              >
                {minutes.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={dropdownStyle}
                aria-label="AM/PM"
              >
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Location Section */}
        <div style={{ 
          backgroundColor: colors.background, 
          padding: "1.5rem", 
          borderRadius: "8px",
          marginBottom: "1rem"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem", color: colors.text }}>
            Venue Information
          </h3>
          
          {/* Quick Select Venues */}
          {recentVenues.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ ...labelStyle, marginBottom: "0.5rem" }}>Quick Select:</label>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {recentVenues.map((venue, index) => (
                  <button 
                    key={index}
                    type="button"
                    onClick={() => handleVenueSelect(venue)}
                    style={venueButtonStyle}
                  >
                    {venue.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Location Name */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Venue Name *</label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              required
              style={inputStyle}
              placeholder="Enter venue name"
            />
          </div>
          
          {/* Location Address */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Venue Address *</label>
            <input
              type="text"
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              required
              style={inputStyle}
              placeholder="Enter full address"
            />
          </div>
        </div>
        
        {/* Advanced Options Toggle */}
        <button 
          type="button" 
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            background: "none",
            border: "none",
            color: colors.primary,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: 0,
            fontSize: "0.875rem"
          }}
        >
          {showAdvanced ? "Hide Advanced Options" : "Show Advanced Options"} 
          <span style={{ marginLeft: "0.25rem" }}>{showAdvanced ? "▲" : "▼"}</span>
        </button>
        
        {/* Advanced Options */}
        {showAdvanced && (
          <div style={{ 
            backgroundColor: colors.background, 
            padding: "1.5rem", 
            borderRadius: "8px",
            marginBottom: "1rem"
          }}>
            <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem", color: colors.text }}>
              Additional Information
            </h3>
            
            {/* Game Status */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Game Status</label>
              <select
                value={gameStatus}
                onChange={(e) => setGameStatus(e.target.value)}
                style={{ ...inputStyle, backgroundColor: "white" }}
              >
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="postponed">Postponed</option>
              </select>
            </div>
            
            {/* Notes */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }}
                placeholder="Enter any additional information about the game..."
              />
            </div>
          </div>
        )}
        
        {/* Buttons */}
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          {isEditMode && (
            <button 
              type="button" 
              onClick={onCancelEdit}
              style={{
                ...secondaryButtonStyle,
                flex: 1
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            style={{
              ...primaryButtonStyle,
              flex: 2,
              backgroundColor: isSubmitting ? colors.textLight : colors.primary,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              "Saving..."
            ) : (
              isEditMode ? "Update Game" : "Schedule Game"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ScheduleNewGame;